import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';

import { LinearProgress, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';

import { HeaderUnderline } from 'components/GlobalStyles/styles';
import Loader from 'components/Loader';

import { MessageType } from '../../components/SnackBar';
import {
  useLazySyncActivitiesQuery,
  useLazySyncCompanyFilesQuery,
  useLazySyncCustomersQuery,
  useLazySyncEmployeePayrollQuery,
  useLazySyncEmployeesQuery,
  useLazySyncJobsFromMYOBQuery,
  useLazySyncPayrollCategoryQuery,
  useSyncEmployeePayrollPollingQuery,
  useSyncEmployeePollingQuery,
  useSyncJobsToMYOBMutation,
  useUpdateJobsInMYOBMutation,
} from '../../services/busmanApi';

import SyncWorklogsModal, { WorklogsSyncProgress } from './component/SyncWorklogsModal';
import useMyobTaskSync, { MyobTaskSync } from './hooks/useMyobTaskSync';
import SyncButton from './component';
import { ErrorLog, MYOBSyncContainer, SyncBox } from './styles';
interface MYOBSyncPageProps {
  setMessage: (message: MessageType | null) => void;
}

export interface SyncLogType {
  type: string;
  message: string;
  timeStamp: string;
}

interface SyncBoxComponentProps {
  title: string;
  handleSync: () => void;
  disabled?: boolean;
  progress?: React.ReactNode;
}

// Declared outside the page so it is not remounted on every render, which would
// restart the progress bar animation on each poll tick
const SyncBoxComponent = ({ title, handleSync, disabled, progress }: SyncBoxComponentProps) => (
  <Box>
    <SyncBox>
      <Typography variant="subtitle1">{title}</Typography>
      <SyncButton onClick={handleSync} disabled={disabled} />
    </SyncBox>
    {progress}
  </Box>
);

function MYOBSyncPage({ setMessage }: MYOBSyncPageProps) {
  const theme = useTheme();
  const [syncConfirmModal, setSyncConfirmModal] = React.useState(false);
  const [errorLogs, setErrorLogs] = React.useState<SyncLogType[]>([]);
  const [worklogsSyncing, setWorklogsSyncing] = React.useState(false);
  const [worklogsProgress, setWorklogsProgress] = React.useState<WorklogsSyncProgress | null>(null);
  const lastLogRef = React.useRef<HTMLElement | null>(null);

  const [syncJobsToMYOB, { isLoading: isSyncingJobsToMYOB }] = useSyncJobsToMYOBMutation();
  const [updateJobsInMYOBMutation, { isLoading: isUpdatingJobsInMYOB }] = useUpdateJobsInMYOBMutation();
  // const [syncWorklogsToMYOB, { isLoading: isSyncingWorklogsToMYOB }] = useSyncWorklogsToMYOBMutation();
  const [syncActivities, { isFetching: isSyncingActivities }] = useLazySyncActivitiesQuery();
  const [syncCompanyFiles, { isFetching: isSyncingCompanyFiles }] = useLazySyncCompanyFilesQuery();
  const [syncCustomers, { isFetching: isSyncingCustomers }] = useLazySyncCustomersQuery();
  const [startEmployeeSync] = useLazySyncEmployeesQuery();
  const [startEmployeePayrollSync] = useLazySyncEmployeePayrollQuery();
  const [syncJobsFromMYOB, { isFetching: isSyncingJobs }] = useLazySyncJobsFromMYOBQuery();
  const [syncPayrollCategory, { isFetching: isSyncingPayrollCategory }] = useLazySyncPayrollCategoryQuery();

  // Appended straight to the list rather than relayed through a single state slot, so a
  // task reporting several lines in one tick keeps all of them
  const handleSetLogMessage = (type: string, message: string) => {
    setErrorLogs((logs) => [...logs, { timeStamp: `[ ${dayjs().format('HH:mm')} ]`, type, message }]);
  };

  // Employee and payroll syncs are background tasks - they return a task id that we poll for progress
  const handleSettled = (type: 'success' | 'error', msg: string) => setMessage({ type, msg });

  const employeeSync = useMyobTaskSync({
    label: 'Employees',
    successMessage: 'Employees synced successfully',
    errorMessage: 'Error syncing employees',
    startSync: startEmployeeSync,
    usePollingQuery: useSyncEmployeePollingQuery,
    onLog: handleSetLogMessage,
    onSettled: handleSettled,
  });

  const employeePayrollSync = useMyobTaskSync({
    label: 'Employee Payroll',
    successMessage: 'Employee Payroll synced successfully',
    errorMessage: 'Error syncing Employee Payroll',
    startSync: startEmployeePayrollSync,
    usePollingQuery: useSyncEmployeePayrollPollingQuery,
    onLog: handleSetLogMessage,
    onSettled: handleSettled,
  });

  const handleUpdateJobsInMYOB = async () => {
    handleSetLogMessage('success', 'Updating Jobs in Myob...');
    const result = await updateJobsInMYOBMutation({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error updating Jobs in Myob');
      setMessage({ type: 'error', msg: 'Error updating jobs in myob' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Updating Jobs in Myob Complete!');
      setMessage({ type: 'success', msg: 'Jobs updated in MYOB successfully' });
    }
  };

  const handleSyncActivities = async () => {
    handleSetLogMessage('success', 'Syncing Activities...');
    const result = await syncActivities({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Syncing Activities Failed!');
      setMessage({ type: 'error', msg: 'Error syncing activities' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Activities Successful!');
      setMessage({ type: 'success', msg: 'Activities synced successfully' });
    }
  };

  // const handleSyncWorklogsToMYOB = async () => {
  //   const result = await syncWorklogsToMYOB({});

  //   if ('error' in result) setMessage({ type: 'error', msg: 'Error syncing worklogs to myob' });

  //   if ('data' in result) {
  //     setMessage({ type: 'success', msg: 'Worklogs synced to MYOB successfully' });
  //   }
  // };

  const handleSyncCompanyFiles = async () => {
    handleSetLogMessage('success', 'Syncing Company Files...');
    const result = await syncCompanyFiles({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error syncing Company Files');
      setMessage({ type: 'error', msg: 'Error syncing company files' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Company Files Complete!');
      setMessage({ type: 'success', msg: 'Company files synced successfully' });
    }
  };

  const handleSyncCustomers = async () => {
    handleSetLogMessage('success', 'Syncing Customers...');
    const result = await syncCustomers({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error syncing Customers');
      setMessage({ type: 'error', msg: 'Error syncing customers' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Customers Complete!');
      setMessage({ type: 'success', msg: 'Customers synced successfully' });
    }
  };

  const handleSyncJobsFromMYOB = async () => {
    handleSetLogMessage('success', 'Syncing Jobs From Myob...');
    const result = await syncJobsFromMYOB({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error syncing Jobs From Myob');
      setMessage({ type: 'error', msg: 'Error syncing jobs' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Jobs From Myob Complete!');
      setMessage({ type: 'success', msg: 'Jobs synced successfully' });
    }
  };

  const handleSyncJobsToMYOB = async () => {
    handleSetLogMessage('success', 'Syncing Jobs to Myob...');
    const result = await syncJobsToMYOB({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error syncing Jobs to Myob');
      setMessage({ type: 'error', msg: 'Error syncing jobs to myob' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Jobs to Myob Complete!');
      setMessage({ type: 'success', msg: 'Jobs synced to MYOB successfully' });
    }
  };

  const handleSyncPayrollCategory = async () => {
    handleSetLogMessage('success', 'Syncing Payroll Category...');
    const result = await syncPayrollCategory({});

    if ('error' in result) {
      handleSetLogMessage('error', 'Error syncing Payroll Category');
      setMessage({ type: 'error', msg: 'Error syncing payroll category' });
    }

    if ('data' in result) {
      handleSetLogMessage('success', 'Syncing Payroll Category Complete!');
      setMessage({ type: 'success', msg: 'Payroll Category synced successfully' });
    }
  };

  const handleOpenSyncConfirm = () => {
    setSyncConfirmModal(true);
  };

  const isSyncing = () => {
    const syncing =
      isUpdatingJobsInMYOB ||
      isSyncingActivities ||
      isSyncingCompanyFiles ||
      isSyncingCustomers ||
      isSyncingJobs ||
      isSyncingJobsToMYOB ||
      isSyncingPayrollCategory;

    return syncing;
  };

  // Scroll to the last log
  React.useEffect(() => {
    if (lastLogRef.current) {
      lastLogRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errorLogs]);

  const renderSyncProgress = (sync: Pick<MyobTaskSync, 'isSyncing' | 'percent' | 'message'>) =>
    sync.isSyncing ? (
      <Box sx={{ marginBottom: theme.spacing(2) }}>
        <LinearProgress variant={sync.percent > 0 ? 'determinate' : 'indeterminate'} value={sync.percent} />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {sync.message || 'Starting sync...'}
          {sync.percent > 0 ? ` (${sync.percent}%)` : ''}
        </Typography>
      </Box>
    ) : null;

  return (
    <>
      <Helmet>
        <title>EEA</title>
        <meta name="Busman" content="EEA-MYOB-Sync" />
      </Helmet>
      <Loader open={isSyncing()} />
      {syncConfirmModal && (
        <SyncWorklogsModal
          open={syncConfirmModal}
          onClose={() => setSyncConfirmModal(false)}
          setMessage={setMessage}
          setLogMessage={handleSetLogMessage}
          setWorklogsSyncing={setWorklogsSyncing}
          setWorklogsProgress={setWorklogsProgress}
        />
      )}
      <MYOBSyncContainer>
        <Box sx={{ backgroundColor: theme.palette.background.paper, height: 'fit-content', flex: 2 }}>
          <HeaderUnderline>
            <Typography variant="h4">MYOB Sync</Typography>
          </HeaderUnderline>
          <Box sx={{ padding: theme.spacing(2) }}>
            <SyncBoxComponent title="Sync All Company files" handleSync={handleSyncCompanyFiles} />
            <SyncBoxComponent title="Sync All Customers" handleSync={handleSyncCustomers} />
            <SyncBoxComponent
              title="Sync All Employees"
              handleSync={employeeSync.start}
              disabled={employeeSync.isSyncing}
              progress={renderSyncProgress(employeeSync)}
            />
            <SyncBoxComponent
              title="Sync All Employees Payroll Details"
              handleSync={employeePayrollSync.start}
              disabled={employeePayrollSync.isSyncing}
              progress={renderSyncProgress(employeePayrollSync)}
            />
            <SyncBoxComponent title="Sync Jobs From MYOB" handleSync={handleSyncJobsFromMYOB} />
            <SyncBoxComponent title="Sync Activities" handleSync={handleSyncActivities} />
            <SyncBoxComponent title="Sync Payroll Category" handleSync={handleSyncPayrollCategory} />
            <SyncBoxComponent
              title="Sync Worklogs"
              handleSync={handleOpenSyncConfirm}
              disabled={worklogsSyncing}
              progress={renderSyncProgress({
                isSyncing: worklogsSyncing,
                percent: worklogsProgress?.percent ?? 0,
                message: worklogsProgress?.message,
              })}
            />
            <SyncBoxComponent title="Sync Job to MYOB" handleSync={handleSyncJobsToMYOB} />
            <SyncBoxComponent title="Update Job in MYOB" handleSync={handleUpdateJobsInMYOB} />
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            height: '100%',
            maxHeight: '25%',
            minHeight: '25%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('sm')]: {
              width: '100%',
              maxHeight: 'none',
              height: '1000px',
            },
          }}>
          <HeaderUnderline>
            <Typography variant="h4">Log </Typography>
          </HeaderUnderline>
          <Box
            sx={{ padding: theme.spacing(2), height: '100%', maxHeight: '100%', overflow: 'hidden', display: 'flex' }}>
            <ErrorLog>
              {errorLogs.map((log, index) => (
                <Typography
                  key={index}
                  ref={index === errorLogs.length - 1 ? lastLogRef : null}
                  variant="h6"
                  sx={{
                    color:
                      log.type === 'error'
                        ? theme.palette.busmanColors.busmanRed
                        : theme.palette.busmanColors.busmanGreen,
                  }}>
                  {log.timeStamp} - {log.message}
                </Typography>
              ))}
            </ErrorLog>
          </Box>
        </Box>
      </MYOBSyncContainer>
    </>
  );
}

export default MYOBSyncPage;
